import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { detectFilteredContent, getFilterErrorMessage } from '../utils/messageFilter';
import { recordProhibitedAttempt, checkRateLimit } from '../utils/rateLimiter';
import { addMessageToBuffer } from '../utils/conversationBuffer';

/**
 * Message Service - Handles all operations related to messages between brands and influencers
 */
const messageService = {
  /**
   * Create a conversation ID from two user IDs
   * @param {string} uid1 - First user ID
   * @param {string} uid2 - Second user ID
   * @returns {string} - Conversation ID
   */
  createConversationId: (uid1, uid2) => {
    // Sort UIDs to ensure consistent conversation IDs regardless of order
    return [uid1, uid2].sort().join('_');
  },

  /**
   * Send a message
   * @param {string} senderId - Sender user ID
   * @param {string} receiverId - Receiver user ID
   * @param {string} text - Message text
   * @param {Array} attachments - Array of attachment URLs
   * @param {string} campaignId - Optional campaign ID if message is related to a campaign
   * @returns {Promise<object>} - Created message object with ID
   * @throws {Error} - If message contains filtered content
   */
  sendMessage: async (senderId, receiverId, text, attachments = [], campaignId = null) => {
    try {
      // Check if user is rate limited before processing the message
      const rateLimitStatus = checkRateLimit(senderId);
      
      if (rateLimitStatus.isBlocked) {
        throw new Error(`You've made too many attempts to send prohibited content. Please try again in ${rateLimitStatus.remainingTime} minutes.`);
      }
      
      // Create conversation ID to use with buffer and later operations
      const conversationId = messageService.createConversationId(senderId, receiverId);
      
      // First validate the individual message content for security
      const contentCheck = detectFilteredContent(text);
      
      // If individual message contains filtered content, reject it and record the attempt
      if (!contentCheck.isClean) {
        // Record this prohibited content attempt
        const updatedStatus = recordProhibitedAttempt(senderId);
        
        // Generate appropriate error message based on attempt count
        let errorMessage = getFilterErrorMessage(contentCheck);
        
        if (updatedStatus.isBlocked) {
          errorMessage = `${errorMessage}. You've exceeded the maximum allowed attempts. Your messaging is temporarily restricted for ${updatedStatus.remainingTime} minutes.`;
        } else if (updatedStatus.attemptsRemaining <= 2) {
          errorMessage = `${errorMessage}. Warning: You have ${updatedStatus.attemptsRemaining} attempts remaining before temporary messaging restrictions.`;
        }
        
        throw new Error(errorMessage || 'Message contains prohibited content');
      }
      
      // Check for prohibited content across multiple messages using the conversation buffer
      const bufferCheck = addMessageToBuffer(conversationId, senderId, text);
      
      // If combined messages contain filtered content, reject and record the attempt
      if (!bufferCheck.isClean) {
        // Record this prohibited content attempt
        const updatedStatus = recordProhibitedAttempt(senderId);
        
        // Generate appropriate error message for multi-message violation
        let errorMessage = `Prohibited content detected across multiple messages: ${getFilterErrorMessage(bufferCheck)}`;
        
        if (updatedStatus.isBlocked) {
          errorMessage = `${errorMessage}. You've exceeded the maximum allowed attempts. Your messaging is temporarily restricted for ${updatedStatus.remainingTime} minutes.`;
        } else if (updatedStatus.attemptsRemaining <= 2) {
          errorMessage = `${errorMessage}. Warning: You have ${updatedStatus.attemptsRemaining} attempts remaining before temporary messaging restrictions.`;
        }
        
        throw new Error(errorMessage);
      }
      
      // Check if conversation exists
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      
      // Create messages collection reference
      const messagesRef = collection(db, 'messages');
      
      // Create message data
      const messageData = {
        conversationId,
        senderId,
        receiverId,
        text,
        attachments,
        sentAt: serverTimestamp(),
        readAt: null,
        isDeleted: false,
        securityChecked: true  // Flag to indicate message passed security checks
      };
      
      // Add campaign ID if provided
      if (campaignId) {
        messageData.campaignId = campaignId;
      }
      
      // Add message to messages collection
      const messageRef = await addDoc(messagesRef, messageData);
      
      // Update or create conversation document
      if (conversationDoc.exists()) {
        // Update existing conversation
        await updateDoc(conversationRef, {
          lastMessageId: messageRef.id,
          lastMessageText: text.length > 100 ? text.substring(0, 97) + '...' : text,
          lastMessageSentAt: serverTimestamp(),
          lastMessageSenderId: senderId,
          [`unreadCounts.${receiverId}`]: (conversationDoc.data().unreadCounts?.[receiverId] || 0) + 1
        });
      } else {
        // Create new conversation document since it doesn't exist
        // We must use setDoc instead of updateDoc for non-existent documents
        const { setDoc } = await import('firebase/firestore');
        await setDoc(conversationRef, {
          id: conversationId,
          participants: [senderId, receiverId],
          lastMessageId: messageRef.id,
          lastMessageText: text.length > 100 ? text.substring(0, 97) + '...' : text,
          lastMessageSentAt: serverTimestamp(),
          lastMessageSenderId: senderId,
          unreadCounts: {
            [receiverId]: 1,
            [senderId]: 0
          },
          createdAt: serverTimestamp()
        });
      }
      
      // Log successful message after security checks

      
      return { id: messageRef.id, ...messageData };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * Get messages from a conversation
   * @param {string} conversationId - Conversation ID
   * @param {object} options - Query options (limit, before, after)
   * @returns {Promise<Array>} - Array of message objects
   */
  getMessages: async (conversationId, options = {}) => {
    try {
      const messagesRef = collection(db, 'messages');
      
      // Build query
      let q = query(
        messagesRef, 
        where('conversationId', '==', conversationId),
        where('isDeleted', '==', false),
        orderBy('sentAt', 'desc')
      );
      
      // Add limit if provided
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const querySnapshot = await getDocs(q);
      
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort messages by sentAt in ascending order for display
      return messages.sort((a, b) => a.sentAt - b.sentAt);
    } catch (error) {
      console.error('Error getting messages:', error);
      throw error;
    }
  },

  /**
   * Get conversations for a user
   * @param {string} userId - User ID
   * @param {object} options - Query options (limit, orderBy)
   * @returns {Promise<Array>} - Array of conversation objects
   */
  getConversations: async (userId, options = {}) => {
    try {
      const conversationsRef = collection(db, 'conversations');
      
      // Build query to find conversations where user is a participant
      let q = query(
        conversationsRef, 
        where('participants', 'array-contains', userId),
        orderBy('lastMessageSentAt', 'desc')
      );
      
      // Add limit if provided
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const querySnapshot = await getDocs(q);
      
      const conversations = [];
      querySnapshot.forEach((doc) => {
        conversations.push({ id: doc.id, ...doc.data() });
      });
      
      return conversations;
    } catch (error) {
      console.error('Error getting conversations:', error);
      throw error;
    }
  },

  /**
   * Mark messages as read
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - User ID marking messages as read
   * @returns {Promise} - Promise that resolves when messages are marked as read
   */
  markMessagesAsRead: async (conversationId, userId) => {
    try {
      // Update conversation unread count
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCounts.${userId}`]: 0
      });
      
      // Get unread messages
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef, 
        where('conversationId', '==', conversationId),
        where('receiverId', '==', userId),
        where('readAt', '==', null)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Mark each message as read
      const updatePromises = [];
      querySnapshot.forEach((doc) => {
        updatePromises.push(
          updateDoc(doc.ref, {
            readAt: serverTimestamp()
          })
        );
      });
      
      await Promise.all(updatePromises);
      
      return querySnapshot.size; // Return number of messages marked as read
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  },

  /**
   * Delete a message (soft delete)
   * @param {string} messageId - Message ID
   * @returns {Promise} - Promise that resolves when message is deleted
   */
  deleteMessage: async (messageId) => {
    try {
      const messageRef = doc(db, 'messages', messageId);
      await updateDoc(messageRef, {
        isDeleted: true,
        deletedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  },

  /**
   * Get unread message count for a user
   * @param {string} userId - User ID
   * @returns {Promise<number>} - Total unread message count
   */
  getUnreadMessageCount: async (userId) => {
    try {
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef, 
        where('participants', 'array-contains', userId)
      );
      
      const querySnapshot = await getDocs(q);
      
      let totalUnread = 0;
      querySnapshot.forEach((doc) => {
        const unreadCounts = doc.data().unreadCounts || {};
        totalUnread += unreadCounts[userId] || 0;
      });
      
      return totalUnread;
    } catch (error) {
      console.error('Error getting unread message count:', error);
      throw error;
    }
  }
};

export default messageService;
